import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';

interface ToggleProps {
    id?: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    label?: React.ReactNode;
    labelClassName?: string;
    required?: boolean;
}

export function Toggle({
    id: idProp,
    checked,
    onCheckedChange,
    disabled = false,
    label,
    labelClassName,
    required,
}: ToggleProps) {
    const autoId = React.useId();
    const switchId = idProp ?? (label ? autoId : undefined);

    const switchRoot = (
        <SwitchPrimitive.Root
            id={switchId}
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-required={required ? true : undefined}
            className="flex h-[18px] w-[32px] flex-shrink-0 cursor-pointer items-center rounded-[64px] bg-grey-500 p-[2px] outline-none transition-colors duration-100 data-[state=checked]:bg-white-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
            <SwitchPrimitive.Thumb className="block h-[14px] w-[14px] shrink-0 rounded-[16px] bg-grey-300 transition-transform duration-100 will-change-transform data-[state=checked]:translate-x-[14px] data-[state=checked]:bg-grey-700" />
        </SwitchPrimitive.Root>
    );

    if (!label) {
        return switchRoot;
    }

    return (
        <div className="flex flex-row items-center gap-2">
            <Label
                htmlFor={switchId}
                required={Boolean(required)}
                className={cn(
                    disabled
                        ? 'cursor-not-allowed text-grey-300'
                        : 'cursor-pointer',
                    labelClassName,
                )}
            >
                {label}
            </Label>
            {switchRoot}
        </div>
    );
}
